import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Users, GraduationCap, CalendarClock, DollarSign, Activity } from "lucide-react";

const monthLabel = (m) => {
  if (!m) return "—";
  const [y, mo] = m.split("-");
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${names[Number(mo) - 1]} ${y}`;
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [activity, setActivity] = useState(null);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    api.get("/admin/overview").then(({ data: d }) => setData(d)).catch(() => {});
    api.get("/admin/activity").then(({ data: d }) => setActivity(d)).catch(() => {});
  }, []);

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  const tiles = [
    { label: "Coaches", value: data?.total_coaches ?? "—", icon: Users, test: "admin-stat-coaches" },
    { label: "Students", value: data?.total_students ?? "—", icon: GraduationCap, test: "admin-stat-students" },
    { label: "Lessons", value: data?.total_lessons ?? "—", icon: CalendarClock, test: "admin-stat-lessons" },
    { label: "Revenue", value: data ? `$${Number(data.revenue || 0).toFixed(0)}` : "—", icon: DollarSign, test: "admin-stat-revenue" },
  ];

  const s = activity?.summary;
  const activityTiles = [
    { label: "Captures (30d)", value: s?.captures_30d ?? "—", test: "admin-act-captures30" },
    { label: "Active coaches (30d)", value: s?.active_coaches_30d ?? "—", test: "admin-act-coaches30" },
    { label: "Active students (30d)", value: s?.active_students_30d ?? "—", test: "admin-act-students30" },
    { label: "Capture minutes", value: s?.total_capture_minutes ?? "—", test: "admin-act-minutes" },
  ];

  const timeline = Object.entries(activity?.timeline || {});
  const maxTimeline = Math.max(1, ...timeline.map(([, n]) => n));

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "activity", label: "Activity" },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/60 border-b border-white/10 px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[#38BDF8] grid place-items-center">
            <span className="font-display font-black text-[#0A0A0A] text-lg leading-none">P</span>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Platform · Admin</div>
            <div className="font-display text-xl font-black uppercase tracking-tight">{user?.name}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/login" className="text-xs text-[#A3A3A3] hover:text-white" data-testid="admin-switch-link">Switch account</Link>
          <button onClick={handleLogout} className="btn-secondary px-4 py-2 text-xs inline-flex items-center gap-2" data-testid="admin-logout-button">
            <LogOut size={14} /> Log out
          </button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto p-8 space-y-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Overview</div>
            <h1 className="font-display text-5xl font-black uppercase tracking-tighter mt-2">Platform Snapshot</h1>
            <p className="text-[#A3A3A3] mt-3 text-sm">All coaches and students using PBCOACH.APP.</p>
          </div>
          <div className="flex gap-1 card-surface p-1" data-testid="admin-tabs">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-widest rounded transition-colors ${
                  tab === t.id ? "bg-[#38BDF8] text-[#0A0A0A]" : "text-[#A3A3A3] hover:text-white"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {tab === "overview" && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {tiles.map((t) => {
                const I = t.icon;
                return (
                  <div key={t.label} className="card-surface p-6" data-testid={t.test}>
                    <I size={18} className="text-[#38BDF8]" aria-hidden="true" />
                    <div className="font-display text-5xl font-black mt-4">{t.value}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] mt-2">{t.label}</div>
                  </div>
                );
              })}
            </div>

            <section>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold mb-3">Coaches</div>
              <div className="card-surface overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[#A3A3A3] uppercase text-xs tracking-widest border-b border-white/10">
                      <th className="text-left py-3 px-4">Name</th>
                      <th className="text-left py-3 px-4">Email</th>
                      <th className="text-right py-3 px-4">Students</th>
                      <th className="text-right py-3 px-4">Lessons</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.coaches || []).map((c) => (
                      <tr key={c.user_id} className="border-b border-white/5 hover:bg-white/[0.03]" data-testid={`admin-coach-${c.user_id}`}>
                        <td className="py-3 px-4 font-bold">{c.name}</td>
                        <td className="py-3 px-4 text-[#A3A3A3]">{c.email}</td>
                        <td className="py-3 px-4 text-right font-display font-bold text-lg">{c.students_count}</td>
                        <td className="py-3 px-4 text-right font-display font-bold text-lg">{c.lessons_count}</td>
                      </tr>
                    ))}
                    {(data?.coaches || []).length === 0 && (
                      <tr><td colSpan={4} className="py-6 px-4 text-center text-[#A3A3A3]">No coaches yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold mb-3">Students</div>
              <div className="card-surface overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[#A3A3A3] uppercase text-xs tracking-widest border-b border-white/10">
                      <th className="text-left py-3 px-4">Name</th>
                      <th className="text-left py-3 px-4">Email</th>
                      <th className="text-left py-3 px-4">Coach</th>
                      <th className="text-right py-3 px-4">DUPR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.students || []).map((s) => (
                      <tr key={s.user_id} className="border-b border-white/5 hover:bg-white/[0.03]" data-testid={`admin-student-${s.user_id}`}>
                        <td className="py-3 px-4 font-bold">{s.name}</td>
                        <td className="py-3 px-4 text-[#A3A3A3]">{s.email}</td>
                        <td className="py-3 px-4">{s.coach_name}</td>
                        <td className="py-3 px-4 text-right font-display font-bold text-lg">{s.skill_level ?? "—"}</td>
                      </tr>
                    ))}
                    {(data?.students || []).length === 0 && (
                      <tr><td colSpan={4} className="py-6 px-4 text-center text-[#A3A3A3]">No students yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}

        {tab === "activity" && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {activityTiles.map((t) => (
                <div key={t.label} className="card-surface p-6" data-testid={t.test}>
                  <Activity size={18} className="text-[#38BDF8]" aria-hidden="true" />
                  <div className="font-display text-4xl font-black mt-4">{t.value}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] mt-2">{t.label}</div>
                </div>
              ))}
            </div>

            {timeline.length > 0 && (
              <section>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold mb-3">Captures per month</div>
                <div className="card-surface p-6 space-y-2" data-testid="admin-activity-timeline">
                  {timeline.map(([m, n]) => (
                    <div key={m} className="flex items-center gap-3">
                      <div className="w-20 text-right text-[#A3A3A3] text-xs">{monthLabel(m)}</div>
                      <div className="flex-1 h-4 bg-white/5 rounded overflow-hidden">
                        <div
                          className="h-full bg-[#38BDF8]/70 rounded"
                          style={{ width: `${Math.round((n / maxTimeline) * 100)}%` }}
                        />
                      </div>
                      <div className="w-10 text-right font-display font-bold text-sm">{n}</div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold mb-3">Coach activity</div>
              <div className="card-surface overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[#A3A3A3] uppercase text-xs tracking-widest border-b border-white/10">
                      <th className="text-left py-3 px-4">Name</th>
                      <th className="text-right py-3 px-4">Students</th>
                      <th className="text-right py-3 px-4">Captures</th>
                      <th className="text-right py-3 px-4">Minutes</th>
                      <th className="text-right py-3 px-4">Lessons</th>
                      <th className="text-right py-3 px-4">Last capture</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(activity?.coaches || []).map((c) => (
                      <tr key={c.user_id} className="border-b border-white/5 hover:bg-white/[0.03]" data-testid={`admin-act-coach-${c.user_id}`}>
                        <td className="py-3 px-4 font-bold">{c.name}</td>
                        <td className="py-3 px-4 text-right font-display font-bold">{c.students_count}</td>
                        <td className="py-3 px-4 text-right font-display font-bold">{c.captures}</td>
                        <td className="py-3 px-4 text-right text-[#A3A3A3]">{c.capture_minutes}</td>
                        <td className="py-3 px-4 text-right text-[#A3A3A3]">{c.lessons}</td>
                        <td className="py-3 px-4 text-right text-[#A3A3A3]">{c.last_capture || "—"}</td>
                      </tr>
                    ))}
                    {(activity?.coaches || []).length === 0 && (
                      <tr><td colSpan={6} className="py-6 px-4 text-center text-[#A3A3A3]">No capture activity yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold mb-3">Student activity</div>
              <div className="card-surface overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[#A3A3A3] uppercase text-xs tracking-widest border-b border-white/10">
                      <th className="text-left py-3 px-4">Name</th>
                      <th className="text-left py-3 px-4">Coach</th>
                      <th className="text-right py-3 px-4">Captures</th>
                      <th className="text-right py-3 px-4">Minutes</th>
                      <th className="text-right py-3 px-4">Lessons</th>
                      <th className="text-right py-3 px-4">Progress</th>
                      <th className="text-right py-3 px-4">Last capture</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(activity?.students || []).map((s) => (
                      <tr key={s.user_id} className="border-b border-white/5 hover:bg-white/[0.03]" data-testid={`admin-act-student-${s.user_id}`}>
                        <td className="py-3 px-4 font-bold">{s.name}</td>
                        <td className="py-3 px-4 text-[#A3A3A3]">{s.coach_name}</td>
                        <td className="py-3 px-4 text-right font-display font-bold">{s.captures}</td>
                        <td className="py-3 px-4 text-right text-[#A3A3A3]">{s.capture_minutes}</td>
                        <td className="py-3 px-4 text-right text-[#A3A3A3]">{s.lessons}</td>
                        <td className="py-3 px-4 text-right text-[#A3A3A3]">{s.progress_entries}</td>
                        <td className="py-3 px-4 text-right text-[#A3A3A3]">{s.last_capture || "—"}</td>
                      </tr>
                    ))}
                    {(activity?.students || []).length === 0 && (
                      <tr><td colSpan={7} className="py-6 px-4 text-center text-[#A3A3A3]">No capture activity yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <p className="text-xs text-[#A3A3A3]">
              "Last capture" reflects the most recent motion-capture session; the app does not track logins or page views, so it is a proxy for activity, not a session log.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
